import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { orderBy } from "firebase/firestore";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import { useCoupleCollection } from "../hooks/useCoupleCollection";
import { Fab } from "../components/Fab";
import { Sheet } from "../components/Sheet";
import type { LinkBoard, SavedLink } from "../types";

const ICON_CHOICES = ["🔗", "🍝", "🏨", "✈️", "🏡", "🎁", "🛍️", "📍"];

type BoardSort = "created-desc" | "created-asc" | "alpha";
const SORT_OPTIONS: { value: BoardSort; label: string }[] = [
  { value: "created-desc", label: "Zuletzt angelegt" },
  { value: "created-asc", label: "Zuerst angelegt" },
  { value: "alpha", label: "Alphabetisch" },
];
const SORT_STORAGE_KEY = "wir_linkboards_sort";
function loadSort(): BoardSort {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    return SORT_OPTIONS.some((o) => o.value === raw) ? (raw as BoardSort) : "created-asc";
  } catch {
    return "created-asc";
  }
}
const TINTS = ["plum", "gold", "garnet"] as const;
type Tint = (typeof TINTS)[number];
const TINT_STYLES: Record<Tint, { bg: string; fg: string }> = {
  plum: { bg: "bg-plum-100 dark:bg-night-surface-raised", fg: "text-plum-700 dark:text-night-accent" },
  gold: { bg: "bg-gold-100 dark:bg-night-surface-raised", fg: "text-gold-500" },
  garnet: { bg: "bg-garnet-100 dark:bg-night-surface-raised", fg: "text-garnet-600" },
};

export function Links() {
  const { user } = useAuth();
  const { couple, profile } = useCouple();
  const { items: boards, loading, add } = useCoupleCollection<LinkBoard>(couple?.id ?? null, "linkBoards", orderBy("createdAt", "asc"));

  const [sort, setSort] = useState<BoardSort>(() => loadSort());
  const [sortOpen, setSortOpen] = useState(false);
  function chooseSort(value: BoardSort) {
    setSort(value);
    try {
      localStorage.setItem(SORT_STORAGE_KEY, value);
    } catch {
      // Best-effort — worst case the choice doesn't survive a reload.
    }
    setSortOpen(false);
  }
  const sortedBoards = useMemo(() => {
    if (sort === "created-desc") return [...boards].reverse();
    if (sort === "alpha") return [...boards].sort((a, b) => a.name.localeCompare(b.name));
    return boards;
  }, [boards, sort]);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [name, setName] = useState("");
  const [icon, setIcon] = useState(ICON_CHOICES[0]);
  const [submitting, setSubmitting] = useState(false);

  function openCreate() {
    setName("");
    setIcon(ICON_CHOICES[Math.floor(Math.random() * ICON_CHOICES.length)]);
    setSheetOpen(true);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || !name.trim() || !user || !profile) return;
    setSubmitting(true);
    try {
      await add({ name: name.trim(), icon, createdByUid: user.uid, createdByName: profile.displayName });
      setSheetOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-28 pt-6">
      <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-plum-900 dark:text-night-accent">Links</h1>
      <p className="mt-1 text-sm text-ink-400 dark:text-night-faint">Rezepte, Hotels & mehr nebeneinander vergleichen.</p>

      {boards.length > 1 && (
        <button
          onClick={() => setSortOpen(true)}
          className="mt-4 flex items-center gap-1.5 text-xs font-medium text-ink-600 dark:text-night-muted"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
            <path d="M6 7h8M6 12h5M6 17h2M17 5v14m0 0 3-3m-3 3-3-3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Sortieren: {SORT_OPTIONS.find((o) => o.value === sort)?.label}
        </button>
      )}

      {loading ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Lädt…</p>
      ) : boards.length === 0 ? (
        <div className="mt-8 text-center">
          <p className="text-sm text-ink-400 dark:text-night-faint">Noch keine Sammlung angelegt.</p>
          <button onClick={openCreate} className="mt-4 h-11 rounded-xl bg-plum-700 px-5 text-sm font-semibold text-cream-50">
            Erste Sammlung anlegen
          </button>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-x-3 gap-y-5">
          {sortedBoards.map((board, i) => (
            <BoardCard key={board.id} board={board} index={i} coupleId={couple!.id} />
          ))}
          <button
            onClick={openCreate}
            className={`flex min-h-[9.5rem] flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-cream-200 text-ink-400 transition-colors active:scale-[0.985] dark:border-night-border dark:text-night-faint ${
              boards.length % 2 === 1 ? "" : "mt-6"
            }`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-6 w-6">
              <path d="M12 5v14M5 12h14" strokeLinecap="round" />
            </svg>
            <span className="text-xs font-medium">Neue Sammlung</span>
          </button>
        </div>
      )}

      <Fab onClick={openCreate} label="Neue Sammlung anlegen" />

      <Sheet open={sheetOpen} onClose={() => setSheetOpen(false)} title="Neue Sammlung">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-400 dark:text-night-faint">Symbol</p>
            <div className="flex flex-wrap gap-2">
              {ICON_CHOICES.map((choice) => (
                <button
                  type="button"
                  key={choice}
                  onClick={() => setIcon(choice)}
                  className={`flex h-10 w-10 items-center justify-center rounded-full text-lg transition-colors ${
                    icon === choice ? "bg-plum-700" : "bg-cream-100 dark:bg-night-surface-raised"
                  }`}
                >
                  {choice}
                </button>
              ))}
            </div>
          </div>
          <input
            type="text"
            placeholder="z.B. Pasta-Rezepte"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-12 w-full rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />
          <button
            type="submit"
            disabled={!name.trim() || submitting}
            className="h-12 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50 disabled:opacity-50"
          >
            {submitting ? "Wird angelegt…" : "Sammlung anlegen"}
          </button>
        </form>
      </Sheet>

      <Sheet open={sortOpen} onClose={() => setSortOpen(false)} title="Sortieren">
        <div className="space-y-1.5">
          {SORT_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => chooseSort(o.value)}
              className={`flex h-12 w-full items-center justify-between rounded-xl px-3 text-left text-sm font-medium ${
                sort === o.value ? "bg-plum-100 text-plum-700 dark:bg-night-surface-raised dark:text-night-accent" : "text-ink-900 dark:text-night-text"
              }`}
            >
              {o.label}
              {sort === o.value && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="h-4 w-4">
                  <path d="M5 12.5 9.5 17 19 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </Sheet>
    </main>
  );
}

function BoardCard({ board, index, coupleId }: { board: LinkBoard; index: number; coupleId: string }) {
  const { items } = useCoupleCollection<SavedLink>(coupleId, `linkBoards/${board.id}/links`, orderBy("createdAt", "asc"));
  const tint = TINTS[index % TINTS.length];
  const style = TINT_STYLES[tint];
  const stagger = index % 2 === 1;
  const tilt = index % 3 === 0 ? "" : index % 3 === 1 ? "-rotate-1" : "rotate-1";

  return (
    <Link
      to={`/links/${board.id}`}
      className={`card card-interactive animate-fade-up flex min-h-[9.5rem] flex-col rounded-2xl p-4 ${tilt} ${stagger ? "mt-6" : ""}`}
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <div className="flex items-center gap-2">
        <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-base ${style.bg}`}>{board.icon}</span>
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-ink-900 dark:text-night-text">{board.name}</p>
        {items.length > 0 && (
          <span className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${style.bg} ${style.fg}`}>{items.length}</span>
        )}
      </div>
      {items.length === 0 ? (
        <p className="mt-3 text-xs text-ink-400 dark:text-night-faint">Noch leer</p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {items.slice(0, 3).map((link) => (
            <li key={link.id} className="truncate text-xs text-ink-600 dark:text-night-muted">
              {link.title || link.url}
            </li>
          ))}
          {items.length > 3 && <li className="text-xs text-ink-400 dark:text-night-faint">+{items.length - 3} weitere</li>}
        </ul>
      )}
    </Link>
  );
}
